// src/hr/portal/PoliciesAdmin.js -- HR's policy sign-offs (mockup screen 4, spec §8.6, §16.2 M8, §16.3 S5).
// One row per policy (its latest version): a ring and "19 / 24 signed", the version and upload day,
// and the actions: who has signed (status drawer), a new version (resets everyone to unsigned), open
// the PDF or link. In December, until it's done, a banner asks HR to re-issue the required notices for
// next year. Re-issuing a year that was already done is refused (409, R31); HR can then re-issue anyway,
// which resends with force: true after a second confirm in the page.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiAlertCircle, FiAlertTriangle, FiExternalLink, FiFileText, FiRefreshCw, FiUpload, FiUsers
} from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Btn, Card, EmptyState, Pill, Ring, Skeleton } from '../kit';
import { dateOfTimestamp, shortDate } from '../timeoff/dates';
import { PolicyDrawer, PolicyStatusDrawer } from './PolicyDrawers';

const TONE_COLORS = { ok: '#34d399', warn: '#fbbf24', mute: '#94a3b8' };

function signTone(p) {
  if (!p.assignedCount) return 'mute';
  return p.signedCount >= p.assignedCount ? 'ok' : 'warn';
}

function PolicyRow({ policy, thisYear, onStatus, onVersion }) {
  const tone = signTone(policy);
  const uploaded = dateOfTimestamp(policy.uploadedAt);
  const href = policy.url || hr.policyFileUrl(policy.id);
  const meta = [
    `v${policy.version}`,
    uploaded ? `uploaded ${shortDate(uploaded)}${uploaded.slice(0, 4) !== String(thisYear) ? `, ${uploaded.slice(0, 4)}` : ''}` : null,
    policy.assignTo === 'list' ? `${policy.assigneeIds.length} ${policy.assigneeIds.length === 1 ? 'person' : 'people'}` : 'everyone',
    `${policy.dueDays} days to sign`
  ].filter(Boolean).join(' · ');
  return (
    <li className="hrp-pa__it">
      <Ring
        value={policy.signedCount}
        max={policy.assignedCount || 1}
        size={38}
        stroke={4}
        color={TONE_COLORS[tone]}
        ariaLabel={`${policy.signedCount} of ${policy.assignedCount} signed`}
      />
      <div className="hrp-pa__txt">
        <b>
          {policy.title}
          {policy.isRequiredNotice && <Pill tone="pto" className="hrp-pa__req">Required notice</Pill>}
        </b>
        <span>{meta}</span>
      </div>
      <Pill tone={tone} className="hrp-pa__count">{`${policy.signedCount} / ${policy.assignedCount} signed`}</Pill>
      <div className="hrp-pa__acts">
        <button type="button" className="hrp-link" aria-label={`Who has signed ${policy.title}`} onClick={() => onStatus(policy)}>
          <FiUsers aria-hidden="true" /> Who's signed
        </button>
        <button type="button" className="hrp-link" aria-label={`New version of ${policy.title}`} onClick={() => onVersion(policy)}>
          <FiRefreshCw aria-hidden="true" /> New version
        </button>
        <a className="hrp-link hrp-pa__open" href={href} target="_blank" rel="noopener noreferrer" aria-label={`Open ${policy.title} (new tab)`}>
          {policy.url ? <FiExternalLink aria-hidden="true" /> : <FiFileText aria-hidden="true" />} Open
        </a>
      </div>
    </li>
  );
}

// Re-issue confirm, inline in the page. phase: 'confirm' | 'conflict' (already done for the year).
function ReissuePanel({ year, count, onCancel, onDone }) {
  const [phase, setPhase] = useState('confirm');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const firstRef = useRef(null);
  useEffect(() => { if (firstRef.current) firstRef.current.focus(); }, [phase]);

  const send = async (force) => {
    setBusy(true);
    setMessage('');
    try {
      const result = await hr.admin.policies.reissueNotices(force ? { year, force: true } : { year });
      onDone((result && result.reissued) || 0);
    } catch (err) {
      setBusy(false);
      const status = err && err.response && err.response.status;
      if (status === 409 && !force) setPhase('conflict');
      setMessage(errorText(err, "Couldn't re-issue the notices. Try again."));
    }
  };

  return (
    <div className="hrp-pa__confirm" role="group" aria-label="Re-issue required notices" data-phase={phase}>
      {phase === 'confirm' ? (
        <p>{`Re-issue ${count} required ${count === 1 ? 'notice' : 'notices'} for ${year}? Each gets a new version, so everyone is asked to acknowledge again. Earlier acknowledgments are kept as proof of delivery.`}</p>
      ) : (
        <>
          <p className="hrp-pa__conflict" role="alert"><FiAlertTriangle aria-hidden="true" />{message}</p>
          <p>Re-issuing again resets everyone's acknowledgments a second time.</p>
        </>
      )}
      {phase === 'confirm' && message && <p className="hrp-pa__conflict" role="alert"><FiAlertCircle aria-hidden="true" />{message}</p>}
      <div className="hrp-pa__cbtns">
        <Btn ref={firstRef} variant="ghost" size="sm" onClick={onCancel} disabled={busy}>{phase === 'confirm' ? 'Keep as is' : 'Cancel'}</Btn>
        {phase === 'confirm'
          ? <Btn size="sm" icon={FiRefreshCw} loading={busy} onClick={() => send(false)}>Yes, re-issue</Btn>
          : <Btn variant="danger" size="sm" icon={FiRefreshCw} loading={busy} onClick={() => send(true)}>Re-issue anyway</Btn>}
      </div>
    </div>
  );
}

export function PoliciesAdmin({ today, people, settings, context, onToast }) {
  const [state, setState] = useState({ status: 'loading', rows: null });
  const [drawer, setDrawer] = useState(null); // { policy } -- null policy = new policy
  const [statusOf, setStatusOf] = useState(null);
  const [reissuing, setReissuing] = useState(false);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const rows = await hr.admin.policies.list();
      setState({ status: 'ready', rows: Array.isArray(rows) ? rows : [] });
    } catch (err) {
      setState((s) => ({ ...s, status: 'error' }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = state.rows || [];
  const notices = rows.filter((p) => p.isRequiredNotice);
  const year = Number(String(today).slice(0, 4));
  const december = String(today).slice(5, 7) === '12';
  const target = december ? year + 1 : year;
  const doneFor = settings ? Number(settings.noticesReissuedFor) : NaN;
  const reissuedForTarget = doneFor === target;
  // Like the daily digest (R50): from Dec 1 until the notices were re-issued for next year. A re-issue
  // for this year was last December's, so it doesn't count.
  const showBanner = december && notices.length > 0 && !!settings && doneFor !== target;
  const all = (people && people.data && people.data.people) || [];

  const saved = (text) => {
    setDrawer(null);
    onToast(text);
    load();
  };
  const reissued = (n) => {
    setReissuing(false);
    onToast(`Re-issued ${n} required ${n === 1 ? 'notice' : 'notices'} for ${target}`);
    load();
    if (context && context.reload) context.reload();
  };

  let sub = 'Sign-offs per policy. A new version resets everyone to unsigned.';
  if (state.rows) {
    const behind = rows.filter((p) => p.assignedCount && p.signedCount < p.assignedCount).length;
    sub = `${rows.length} ${rows.length === 1 ? 'policy' : 'policies'} · ${behind ? `${behind} still collecting signatures` : 'all signed'}`;
    if (reissuedForTarget && notices.length) sub += ` · notices re-issued for ${target}`;
  }

  let body;
  if (!state.rows && state.status === 'error') {
    body = (
      <EmptyState compact icon={FiAlertCircle} title="Couldn't load policies" action={<Btn variant="ghost" size="sm" onClick={load}>Try again</Btn>}>
        Something went wrong on our side.
      </EmptyState>
    );
  } else if (!state.rows) {
    body = <Skeleton label="Loading policies" lines={4} height={30} />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState compact icon={FiFileText} title="No policies yet" action={<Btn size="sm" icon={FiUpload} onClick={() => setDrawer({ policy: null })}>Upload a policy</Btn>}>
        Upload a PDF or add a link, and everyone it's for is asked to acknowledge it.
      </EmptyState>
    );
  } else {
    body = (
      <ul className="hrp-pa__list" aria-label="Policies">
        {rows.map((p) => <PolicyRow key={p.id} policy={p} thisYear={year} onStatus={setStatusOf} onVersion={(policy) => setDrawer({ policy })} />)}
      </ul>
    );
  }

  return (
    <div className="hrp-page hrp-pa">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Policies</h1>
          <p className="hrp-sub">{sub}</p>
        </div>
        <div className="hrp-page-head__acts">
          {notices.length > 0 && !showBanner && !reissuing && (
            <Btn variant="ghost" icon={FiRefreshCw} onClick={() => setReissuing(true)}>Re-issue required notices</Btn>
          )}
          <Btn icon={FiUpload} onClick={() => setDrawer({ policy: null })}>Upload policy</Btn>
        </div>
      </div>

      {showBanner && !reissuing && (
        <div className="hrp-banner hrp-pa__banner" role="note">
          <span className="hrp-banner__ic" aria-hidden="true"><FiAlertTriangle /></span>
          <div className="hrp-banner__txt">
            <div className="hrp-banner__title">Re-issue required notices before Dec 31</div>
            <div className="hrp-banner__text">
              {`${notices.length} required ${notices.length === 1 ? 'notice gets' : 'notices get'} a new version for ${target}, so everyone acknowledges again. The per-version acknowledgments prove delivery.`}
            </div>
          </div>
          <Btn size="sm" icon={FiRefreshCw} onClick={() => setReissuing(true)}>{`Re-issue for ${target}`}</Btn>
        </div>
      )}
      {reissuing && <ReissuePanel year={target} count={notices.length} onCancel={() => setReissuing(false)} onDone={reissued} />}

      <Card delay={0} className="hrp-pa__card">
        {state.status === 'error' && state.rows && (
          <div className="hrp-form-err hrp-pa__stale" role="alert">
            <FiAlertCircle aria-hidden="true" /><span>This list didn't refresh.</span>
            <Btn variant="ghost" size="sm" onClick={load}>Try again</Btn>
          </div>
        )}
        {body}
      </Card>

      <PolicyDrawer open={!!drawer} policy={drawer && drawer.policy} people={all} onClose={() => setDrawer(null)} onDone={saved} />
      <PolicyStatusDrawer policy={statusOf} onClose={() => setStatusOf(null)} />
    </div>
  );
}

export default PoliciesAdmin;
